import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, PLATFORM_ID, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { Blog } from '../../models/blog';
import { Brand } from '../../models/brand';
import { Category, FavouriteCategories } from '../../models/category';
import { Coupon } from '../../models/coupon';
import { Deal, CategoryDeal } from '../../models/deal';
import { PageMeta, PageTitle } from '../../models/page-meta';
import { BrandApiService } from '../../services/brand-api.service';
import { CategoryApiService } from '../../services/category-api.service';
import { DealApiService } from '../../services/deal-api.service';
import { PagesApiService } from '../../services/pages-api.service';
import { ReplaceMetaDatePipe } from '../../pipes/replace-meta-date.pipe';

@Component({
  selector: 'app-home',
  imports: [CommonModule, RouterModule, ReplaceMetaDatePipe],
  templateUrl: './home.component.html',
  styleUrl: './home.component.css',
  encapsulation: ViewEncapsulation.None
})
export class HomeComponent implements OnDestroy {
  brands: Brand[] = [];
  categories: Category[] = [];
  favouriteCategories: FavouriteCategories = {} as FavouriteCategories;
  deals: Deal[] = [];
  bannerDeals: Deal[] = [];
  topDeals: Deal[] = [];
  homeDeals: Deal[] = [];
  latestDeals: Deal[] = [];
  homeCoupons: Coupon[] = [];
  latestCoupons: Coupon[] = [];
  categoryDeals: CategoryDeal[] = [];
  topStoreDeals: Deal[] = [];
  blogs: Blog[] = [];
  isStoreCollapsed: boolean = false;
  isCategoryCollapsed: boolean = false;
  pendingCalls: number = 0;
  pageTitles: PageTitle[] = [];
  activeOrderedSections: string[] = [];
  isDataLoaded = false;
  activeSlide = 0;
  activeSideSlide = 0;
  today = new Date();
  pagemeta: PageMeta[] = [];
  faqHtmlContent: string = '';
  private bannerRotationTimer?: ReturnType<typeof setInterval>;
  private sideRotationTimer?: ReturnType<typeof setInterval>;

  @ViewChild('sliderTrack')
  sliderTrack!: ElementRef<HTMLDivElement>;

  @ViewChild('sideSliderTrack')
  sideSliderTrack!: ElementRef<HTMLDivElement>;

  loadAllData(): void {
    this.pendingCalls = 8;

    this.getBannerDeals();
    //this.getPageTitles();
    this.getBrands();
    this.getCategories();
    this.getTopDeals();
    this.getHomeDeals();
    //this.getLatestDeals();
    //this.getLatestCoupons();
    this.getHomeCoupons();
    //this.getTopCategoryDeals();
    this.getBlogs();
    //this.getFavouriteCategories();
    this.getPageMeta();
    //this.getTopStoreDeals();
    this.getSiteInfos();
  }

  ngOnInit(): void {
    this.loadAllData();
  }

  checkIfAllDone(): void {
    this.pendingCalls -= 1;
    if (this.pendingCalls === 0) {
      this.isDataLoaded = true;
    }
  }

  private isSuccessResponse(response: { Message?: string } | null | undefined): boolean {
    return response?.Message === 'Data Fetched Successfully!!!';
  }

  private handleResponse<T>(response: { Message?: string; Data?: T }, onSuccess: (data: T) => void): void {
    if (this.isSuccessResponse(response)) {
      onSuccess(response.Data as T);
    }
    this.checkIfAllDone();
  }

  private setBannerDeals(deals: Deal[]): void {
    const normalizedDeals = deals.filter((deal) => deal.IsBanner).slice(0, 5);
    const [firstDeal, ...remainingDeals] = normalizedDeals;

    this.bannerDeals = firstDeal ? [...remainingDeals, firstDeal] : [];
    if (this.bannerDeals[0]?.DealImageUrl) {
      this.preloadBannerImage(this.bannerDeals[0].DealImageUrl);
    }
    this.startBannerRotation();
  }

  private preloadBannerImage(imageUrl: string): void {
    if (this.dom.head.querySelector(`link[rel="preload"][href="${imageUrl}"]`)) {
      return;
    }

    const preload = this.renderer.createElement('link');
    this.renderer.setAttribute(preload, 'rel', 'preload');
    this.renderer.setAttribute(preload, 'as', 'image');
    this.renderer.setAttribute(preload, 'href', imageUrl);
    this.renderer.setAttribute(preload, 'fetchpriority', 'high');
    this.renderer.appendChild(this.dom.head, preload);
  }

  private startSideRotation(): void {
    if (this.categories.length < 2) {
      return;
    }

    this.stopSideRotation();
    this.sideRotationTimer = setInterval(() => {
      this.selectSideSlide((this.activeSideSlide + 1) % this.categories.length);
    }, 4000);
  }

  private stopSideRotation(): void {
    if (this.sideRotationTimer) {
      clearInterval(this.sideRotationTimer);
      this.sideRotationTimer = undefined;
    }
  }

  private startBannerRotation(): void {
    if (this.bannerDeals.length < 2) {
      return;
    }

    this.stopBannerRotation();
    this.bannerRotationTimer = setInterval(() => {
      this.selectSlide((this.activeSlide + 1) % this.bannerDeals.length);
    }, 4000);
  }

  private stopBannerRotation(): void {
    if (this.bannerRotationTimer) {
      clearInterval(this.bannerRotationTimer);
      this.bannerRotationTimer = undefined;
    }
  }

  ngOnDestroy(): void {
    this.stopBannerRotation();
    this.stopSideRotation();
  }

  onFaqClick(event: Event): void {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const question = target.closest<HTMLButtonElement>('.faq-question');
    if (!question) {
      return;
    }

    const faqContainer = question.closest('.faq-right');
    const faqQuestions = faqContainer?.querySelectorAll<HTMLButtonElement>('.faq-question') ?? [];
    const isExpanded = question.getAttribute('aria-expanded') === 'true';

    faqQuestions.forEach((faqQuestion) => {
      faqQuestion.setAttribute('aria-expanded', 'false');
      const answer = faqQuestion.nextElementSibling as HTMLElement | null;
      if (answer) {
        answer.style.maxHeight = '';
      }
      faqQuestion.closest('.faq-item')?.classList.remove('active');
    });

    if (!isExpanded) {
      question.setAttribute('aria-expanded', 'true');
      const answer = question.nextElementSibling as HTMLElement | null;
      if (answer) {
        answer.style.maxHeight = `${answer.scrollHeight}px`;
      }
      question.closest('.faq-item')?.classList.add('active');
    }
  }

  selectSideSlide(index: number): void {
    const slider = this.sideSliderTrack?.nativeElement;
    this.activeSideSlide = index;

    if (slider) {
      slider.scrollTo({
        left: slider.clientWidth * index,
        behavior: 'smooth'
      });
    }
  }

  onSideSliderScroll(): void {
    const slider = this.sideSliderTrack?.nativeElement;
    if (slider && slider.clientWidth > 0) {
      this.activeSideSlide = Math.round(slider.scrollLeft / slider.clientWidth);
    }
  }

  selectSlide(index: number): void {
    if (!this.bannerDeals.length || !this.sliderTrack) {
      return;
    }

    this.activeSlide = index;

    const slider = this.sliderTrack.nativeElement;
    const slideWidth = slider.clientWidth;

    slider.scrollTo({
      left: slideWidth * index,
      behavior: 'smooth'
    });
  }

  onSliderScroll(): void {
    if (!this.sliderTrack) {
      return;
    }

    const slider = this.sliderTrack.nativeElement;
    const index = Math.round(slider.scrollLeft / slider.clientWidth);

    if (index !== this.activeSlide) {
      this.activeSlide = index;
    }
  }

  constructor(
    private brandApi: BrandApiService,
    private categoryApi: CategoryApiService,
    private dealsApi: DealApiService,
    private pageApi: PagesApiService,
    private meta: Meta,
    private title: Title,
    private replaceMetaPipe: ReplaceMetaDatePipe,
    @Inject(DOCUMENT) private dom: Document,
    private renderer: Renderer2
  ) {
  }

  updateMetaData(data: PageMeta): void {
    const pageTitle = this.replaceMetaPipe.transform(data.PageTitle!);
    const pageDescription = this.replaceMetaPipe.transform(data.PageDescription!);
    const pageUrl = `https://www.dealszo.com/`;
    // const keyWords = data.PageKeywords!;

    // Update the title and meta tags dynamically
    this.title.setTitle(pageTitle);
    this.meta.updateTag({ name: 'title', content: pageTitle });
    this.meta.updateTag({ name: 'description', content: pageDescription });
    // this.meta.updateTag({
    //   name: 'keywords',
    //   content: keyWords,
    // });

    this.meta.updateTag({
      name: 'robots',
      content:
        'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    });

    // Open Graph meta tags
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'dealszo.com' });
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({
      property: 'og:description',
      content: pageDescription,
    });
    // this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({ property: 'og:url', content: pageUrl });

    let link = this.dom.querySelector<HTMLLinkElement>("link[rel='canonical']");
    let alternativeLink = this.dom.querySelector<HTMLLinkElement>("link[rel='alternate']");

    if (link) {
      this.renderer.setAttribute(link, 'href', pageUrl);
    } else {
      link = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'rel', 'canonical');
      this.renderer.setAttribute(link, 'href', pageUrl);
      this.renderer.appendChild(this.dom.head, link);
    }

    if (alternativeLink) {
      this.renderer.setAttribute(alternativeLink, 'href', pageUrl);
    } else {
      alternativeLink = this.renderer.createElement('link');
      this.renderer.setAttribute(alternativeLink, 'rel', 'alternate');
      this.renderer.setAttribute(alternativeLink, 'href', pageUrl);
      this.renderer.appendChild(this.dom.head, alternativeLink);
    }

    if (data.PageSchema && data.PageSchema.trim() !== '') {
      const schema = this.replaceMetaPipe.transform(data.PageSchema);
      const jsonLdScripts = this.dom.head.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      jsonLdScripts.forEach(script => script.remove());
      const temp = this.dom.createElement('div');
      temp.innerHTML = schema;
      const scripts = temp.querySelectorAll(
        'script[type="application/ld+json"]'
      );
      scripts.forEach((scriptTag, index) => {
        const newScript = this.dom.createElement('script');
        newScript.type = 'application/ld+json';
        newScript.id = `schema-jsonld-${index}`;
        newScript.text = scriptTag.textContent || '';
        this.dom.head.appendChild(newScript);
      });
    }
  }

  getPageMeta(): void {
    this.pageApi.getPagesMeta().subscribe((data) => {
      if (this.isSuccessResponse(data)) {
        this.pagemeta = data.Data;
        const pageMeta = this.pagemeta.find((item) => item.PageName?.includes('index'));
        if (pageMeta) {
          this.updateMetaData(pageMeta);
        }
      }
      this.checkIfAllDone();
    });
  }

  getBrands(): void {
    this.brandApi.getStores(1, 1, 30).subscribe((data) => {
      this.handleResponse<Brand[]>(data, (items) => {
        this.brands = items;
      });
    });
  }

  getCategories(): void {
    this.categoryApi.getHomeCategories(1).subscribe((data) => {
      this.handleResponse<Category[]>(data, (items) => {
        this.categories = items;
        this.startSideRotation();
      });
    });
  }

  getTopCategoryDeals(): void {
    this.dealsApi.getTopDeals(0, 1, 0, 1, 2).subscribe((data) => {
      this.handleResponse<CategoryDeal[]>(data, (items) => {
        this.categoryDeals = items;
      });
    });
  }

  getHomeDeals(): void {
    this.dealsApi.getAllDeals(0, 1, 0, 2, 1, false).subscribe((data) => {
      this.handleResponse<Deal[]>(data, (items) => {
        this.homeDeals = items.slice(0, 5);
      });
    });
  }

  getTopDeals(): void {
    this.dealsApi.getAllDeals(0, 1, 0, 1, 2, false).subscribe((data) => {
      this.handleResponse<Deal[]>(data, (items) => {
        this.topDeals = items.slice(0, 9);
      });
    });
  }

  getBannerDeals(): void {
    this.dealsApi.getBannerDeals(2, 1).subscribe((data) => {
      this.handleResponse<Deal[]>(data, (items) => {
        this.setBannerDeals(items);
      });
    });
  }

  getPageTitles(): void {
    this.pageApi.getPageTitles(1).subscribe((data) => {
      this.handleResponse<PageTitle[]>(data, (items) => {
        this.pageTitles = items;
        this.activeOrderedSections = this.pageTitles
          .filter((section) => section.PageName === 'Home Page')
          .sort((a, b) => a.BannerOrder - b.BannerOrder)
          .map((section) => section.BannerName);
      });
    });
  }

  getLatestDeals(): void {
    this.dealsApi.getAllDeals(0, 1, 0, 2, 2, true).subscribe((data) => {
      this.handleResponse<Deal[]>(data, (items) => {
        this.latestDeals = items.slice(0, 5);
      });
    });
  }

  getHomeCoupons(): void {
    this.dealsApi.getAllCoupons(0, true, 0, 2, 1, false, 15).subscribe((data) => {
      this.handleResponse<Coupon[]>(data, (items) => {
        this.homeCoupons = items.slice(0, 15);
      });
    });
  }

  getLatestCoupons(): void {
    this.dealsApi.getAllCoupons(0, true, 0, 2, 2, true, 12).subscribe((data) => {
      this.handleResponse<Coupon[]>(data, (items) => {
        this.latestCoupons = items.slice(0, 5);
      });
    });
  }

  getBlogs(): void {
    this.pageApi.getBlogs(1).subscribe((data) => {
      this.handleResponse<Blog[]>(data, (items) => {
        this.blogs = items;
      });
    });
  }

  getFavouriteCategories(): void {
    this.categoryApi.getFavouriteCategories().subscribe((data) => {
      if (this.isSuccessResponse(data)) {
        this.favouriteCategories = data.Data;
        this.favouriteCategories.IsSuccess = true;
      } else {
        this.favouriteCategories.IsSuccess = false;
      }
      this.checkIfAllDone();
    });
  }

  getSiteInfos() {
    this.pageApi.getSiteInfo().subscribe((data) => {
      if (data.Message === 'Data Fetched Successfully!!!') {
        const faqContent = data.Data.find(
          (s) => s.InfoId == 3
        )!.PageContent;
        this.faqHtmlContent = faqContent
          .replace(/<button(\s[^>]*)?>/gi, '<div$1 role="button" tabindex="0">')
          .replace(/<\/button>/gi, '</div>');
      } else {
      }
    });
  }

  getTopStoreDeals(): void {
    this.dealsApi.getTopStoreDeals().subscribe((data) => {
      this.handleResponse<Deal[]>(data, (items) => {
        this.topStoreDeals = items.slice(0, 5);
      });
    });
  }

  directToBrandPage(coupon: any): void {
    if (coupon) {
      console.log('Coupon object:', coupon);
      let seoName = coupon.SeoName;
      window.location.href = `${seoName}-coupons/`;
    } else {
      console.error('Coupon is undefined or invalid:', coupon);
    }
  }

  directToCategoryPageCoupons(category: Category): void {
    if (category.CategorySEOName) {
      const couponsUrl = `coupons/${category.CategorySEOName}/`;
      window.location.href = couponsUrl;
    } else {
      console.error(
        'Category name is undefined or invalid:',
        category.CategorySEOName
      );
    }
  }

  directToDealCodePage(deal: Deal): void {
    const url = deal.DealUrl;
    window.open(url!, '_blank');
    window.location.href = `deal-code/${deal.DealId}/`;
  }

  directToCouponPage(coupon: Coupon): void {
    const url = coupon.CouponUrl;
    window.open(url!, '_blank');
    window.location.href = `coupon-code/${coupon.CouponId}/`;
  }

  openBlogPage(blog: Blog): void {
    if (blog.BlogUrl) {
      window.open(blog.BlogUrl!);
    } else {
      window.open(`https://blog.dealszo.com`);
    }
  }

}
